import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Grid, Chip, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, CircularProgress, Tooltip, Divider, Badge,
  LinearProgress, Avatar, InputAdornment,
  useTheme, useMediaQuery, Card, CardContent, CardActions,
  Tabs, Tab, Switch, FormControlLabel,
} from "@mui/material";
import {
  AttachMoney, CheckCircle, HourglassEmpty, NotificationsActive,
  Search, FilterList, Refresh, Visibility, ThumbUp, ThumbDown,
  Payment, EventNote, Person, Close, Warning,
  Settings, Edit, Save, Delete, Calculate, Inventory,
  Build, TrendingUp,
} from "@mui/icons-material";
import api from "../../services/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

const ESTATUS_CONFIG = {
  Pendiente:  { color: "#FFC107", bg: "rgba(255,193,7,0.12)",  label: "Pendiente" },
  Pagada:     { color: "#4CAF50", bg: "rgba(76,175,80,0.12)",  label: "Pagada" },
  Cancelado:  { color: "#EF5350", bg: "rgba(239,83,80,0.12)",  label: "Cancelado" },
};

const ANTICIPO_CONFIG = {
  pendiente:  { color: "#90CAF9", bg: "rgba(144,202,249,0.12)", label: "Disponible" },
  solicitado: { color: "#FFA726", bg: "rgba(255,167,38,0.15)",  label: "Solicitado" },
  pagado:     { color: "#4CAF50", bg: "rgba(76,175,80,0.12)",   label: "Pagado" },
  rechazado:  { color: "#EF5350", bg: "rgba(239,83,80,0.12)",   label: "Rechazado" },
};

const StatusChip = ({ value, map }) => {
  const cfg = map[value] ?? { color: "#9E9E9E", bg: "rgba(158,158,158,0.1)", label: value || "—" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "0.72rem" }}
    />
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, label, value, color, sub }) => (
  <Box
    sx={{
      width: { xs: 118, sm: 145 },
      height: { xs: 118, sm: 145 },
      borderRadius: "50%",
      background: `radial-gradient(circle at 35% 35%, ${color}40, ${color}15)`,
      border: `2px solid ${color}50`,
      boxShadow: `0 0 22px ${color}30`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: 0.25, sm: 0.4 },
      mx: "auto",
      transition: "transform .2s",
      "&:hover": { transform: "scale(1.06)" },
    }}
  >
    <Box sx={{ color, lineHeight: 1, "& .MuiSvgIcon-root": { fontSize: { xs: 22, sm: 27 } } }}>
      {icon}
    </Box>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: "1.05rem", sm: "1.3rem" },
        color: "#fff",
        lineHeight: 1,
        textAlign: "center",
        px: 0.5,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.5rem", sm: "0.6rem" },
        color: "rgba(255,255,255,0.75)",
        fontWeight: 600,
        textAlign: "center",
        lineHeight: 1.2,
        px: 1,
      }}
    >
      {label}
    </Typography>
    {sub && (
      <Typography
        sx={{
          fontSize: { xs: "0.45rem", sm: "0.55rem" },
          color: "rgba(255,255,255,0.5)",
          textAlign: "center",
          lineHeight: 1.2,
          px: 1,
        }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

// =============================================================================
// CONFIGURACIÓN DE TARIFAS — constantes y componente ReglaCard
// =============================================================================
const TIPO_REF_CFG = {
  global:   { color: "#64B5F6", label: "Global",   icon: "🌐" },
  paquete:  { color: "#81C784", label: "Paquete",  icon: "📦" },
  servicio: { color: "#CE93D8", label: "Servicio", icon: "🛠️" },
};

const calcComision = (regla, base) => {
  if (!regla || !base) return null;
  const comision =
    regla.tipo_comision === "porcentaje"
      ? (base * parseFloat(regla.valor)) / 100
      : parseFloat(regla.valor);
  const anticipo = (comision * parseFloat(regla.porcentaje_anticipo)) / 100;
  return { comision, anticipo };
};

const ReglaCard = ({ item, tipoRef, regla, globalRegla, calculatorBase, onSaved, onSnack, isGlobal = false }) => {
  const [editing, setEditing]             = useState(false);
  const [form, setForm]                   = useState({});
  const [saving, setSaving]               = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cfg           = TIPO_REF_CFG[tipoRef];
  const hasRule       = !!regla;
  const effectiveRule = regla ?? globalRegla;
  const calc          = calcComision(effectiveRule, calculatorBase);

  const startEdit = () => {
    setForm(
      regla
        ? { ...regla }
        : {
            tipo_referencia:     tipoRef,
            referencia_id:       item.id,
            referencia_nombre:   item.nombre,
            tipo_comision:       globalRegla?.tipo_comision ?? "porcentaje",
            valor:               globalRegla?.valor ?? 10,
            porcentaje_anticipo: globalRegla?.porcentaje_anticipo ?? 25,
            activo:              1,
            notas:               "",
          }
    );
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/comisiones/reglas?id=${form.id}`, form);
      } else {
        await api.post("/comisiones/reglas", form);
      }
      onSnack({ show: true, msg: "Regla guardada correctamente", sev: "success" });
      setEditing(false);
      onSaved();
    } catch {
      onSnack({ show: true, msg: "Error al guardar la regla", sev: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/comisiones/reglas?id=${regla.id}`);
      onSnack({ show: true, msg: "Regla personalizada eliminada", sev: "info" });
      setConfirmDelete(false);
      setEditing(false);
      onSaved();
    } catch {
      onSnack({ show: true, msg: "Error al eliminar la regla", sev: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const previewCalc = calcComision(
    editing ? { ...form, valor: parseFloat(form.valor) || 0 } : null,
    calculatorBase
  );

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${hasRule ? cfg.color + "45" : "rgba(255,255,255,0.08)"}`,
          borderRadius: 2.5,
          background: hasRule
            ? `linear-gradient(145deg, ${cfg.color}08 0%, transparent 100%)`
            : "rgba(255,255,255,0.02)",
          transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: `0 8px 28px ${hasRule ? cfg.color + "22" : "rgba(0,0,0,0.25)"}`,
            borderColor: hasRule ? cfg.color + "70" : "rgba(255,255,255,0.18)",
          },
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Barra de acento superior */}
        <Box
          sx={{
            height: 3,
            borderRadius: "10px 10px 0 0",
            background: hasRule
              ? `linear-gradient(90deg, ${cfg.color}, ${cfg.color}55)`
              : "rgba(255,255,255,0.08)",
          }}
        />

        {/* Punto de estado activo/inactivo */}
        {hasRule && (
          <Box
            sx={{
              position: "absolute", top: 10, right: 10,
              width: 8, height: 8, borderRadius: "50%",
              bgcolor: regla.activo ? "#4CAF50" : "#757575",
              boxShadow: regla.activo ? "0 0 6px #4CAF5099" : "none",
            }}
          />
        )}

        <CardContent sx={{ p: 2, flex: 1 }}>
          {!editing ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.3, pr: 1.5 }}>
                  {item.nombre}
                </Typography>
                <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 600 }}>
                  {cfg.icon} {cfg.label}
                  {item.precio ? ` · ${fmt(item.precio)}` : ""}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 1.5, alignItems: "flex-end" }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.2 }}>Comisión</Typography>
                  <Typography sx={{ fontWeight: 800, color: hasRule ? cfg.color : "rgba(255,255,255,0.3)", fontSize: "1.3rem", lineHeight: 1 }}>
                    {effectiveRule
                      ? effectiveRule.tipo_comision === "porcentaje"
                        ? `${effectiveRule.valor}%`
                        : fmt(effectiveRule.valor)
                      : "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {effectiveRule?.tipo_comision === "porcentaje" ? "porcentaje" : effectiveRule?.tipo_comision === "monto_fijo" ? "monto fijo" : ""}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.2 }}>Anticipo</Typography>
                  <Typography sx={{ fontWeight: 800, color: "#FFC107", fontSize: "1.3rem", lineHeight: 1 }}>
                    {effectiveRule ? `${effectiveRule.porcentaje_anticipo}%` : "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">de la comisión</Typography>
                </Box>

                {calc && (
                  <Box sx={{ ml: "auto", textAlign: "right" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.2 }}>Preview</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: hasRule ? cfg.color : "rgba(255,255,255,0.25)", whiteSpace: "nowrap" }}>
                      {fmt(calc.comision)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#FFC10799", fontSize: "0.68rem" }}>
                      ant. {fmt(calc.anticipo)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {!hasRule && !isGlobal && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.28)", fontStyle: "italic" }}>
                  Usa la regla predeterminada
                </Typography>
              )}
              {hasRule && (
                <Chip
                  label="Personalizada"
                  size="small"
                  sx={{ bgcolor: cfg.color + "18", color: cfg.color, fontWeight: 700, fontSize: "0.62rem", height: 18 }}
                />
              )}
            </>
          ) : (
            /* ── Modo edición ── */
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: cfg.color }}>
                ✏️ {isGlobal ? "Regla Predeterminada" : item.nombre}
              </Typography>

              {/* Tipo toggle */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Tipo de comisión</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[{ val: "porcentaje", label: "% Porcentaje" }, { val: "monto_fijo", label: "$ Monto Fijo" }].map(({ val, label }) => (
                    <Box
                      key={val}
                      onClick={() => setForm((f) => ({ ...f, tipo_comision: val }))}
                      sx={{
                        px: 1.5, py: 0.6, borderRadius: 1.5, cursor: "pointer",
                        fontSize: "0.72rem", fontWeight: 600, transition: "all 0.15s",
                        bgcolor: form.tipo_comision === val ? cfg.color + "25" : "rgba(255,255,255,0.05)",
                        color: form.tipo_comision === val ? cfg.color : "rgba(255,255,255,0.4)",
                        border: `1px solid ${form.tipo_comision === val ? cfg.color + "60" : "transparent"}`,
                        "&:hover": { bgcolor: cfg.color + "15" },
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>

              <TextField
                size="small" fullWidth
                label={form.tipo_comision === "porcentaje" ? "Porcentaje" : "Monto fijo"}
                type="number"
                inputProps={{ min: 0, max: form.tipo_comision === "porcentaje" ? 100 : 9999999, step: 0.5 }}
                value={form.valor}
                onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{form.tipo_comision === "porcentaje" ? "%" : "MXN"}</InputAdornment>,
                }}
              />

              <TextField
                size="small" fullWidth
                label="% de anticipo"
                type="number"
                inputProps={{ min: 0, max: 100, step: 1 }}
                value={form.porcentaje_anticipo}
                onChange={(e) => setForm((f) => ({ ...f, porcentaje_anticipo: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />

              {previewCalc && (
                <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 1.5, p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">Preview con {fmt(calculatorBase)}</Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Comisión</Typography>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: cfg.color }}>{fmt(previewCalc.comision)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Anticipo</Typography>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#FFC107" }}>{fmt(previewCalc.anticipo)}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!form.activo}
                    onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked ? 1 : 0 }))}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: form.activo ? cfg.color : "rgba(255,255,255,0.4)" }}>
                    {form.activo ? "Regla activa" : "Regla inactiva"}
                  </Typography>
                }
              />
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: editing ? "space-between" : "flex-end", gap: 1, mt: "auto" }}>
          {!editing ? (
            <>
              {hasRule && !isGlobal && (
                <Tooltip title="Eliminar regla personalizada">
                  <IconButton size="small" sx={{ color: "#EF5350", opacity: 0.6, "&:hover": { opacity: 1 } }} onClick={() => setConfirmDelete(true)}>
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                size="small" variant="outlined"
                startIcon={<Edit sx={{ fontSize: 14 }} />}
                onClick={startEdit}
                sx={{ fontSize: "0.72rem", color: cfg.color, borderColor: cfg.color + "50", "&:hover": { bgcolor: cfg.color + "10", borderColor: cfg.color } }}
              >
                {hasRule ? "Editar" : "Personalizar"}
              </Button>
            </>
          ) : (
            <>
              <Box>
                {regla && !isGlobal && (
                  <Button size="small" color="error" variant="text" onClick={() => setConfirmDelete(true)} disabled={saving || deleting} sx={{ fontSize: "0.7rem" }}>
                    Eliminar
                  </Button>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Button size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ fontSize: "0.72rem" }}>
                  Cancelar
                </Button>
                <Button
                  size="small" variant="contained" onClick={handleSave} disabled={saving}
                  startIcon={saving ? <CircularProgress size={12} /> : <Save sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: "0.72rem", bgcolor: cfg.color, color: "#000", fontWeight: 700, "&:hover": { bgcolor: cfg.color + "cc" } }}
                >
                  Guardar
                </Button>
              </Box>
            </>
          )}
        </CardActions>
      </Card>

      <Dialog
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        PaperProps={{ sx: { bgcolor: "#1a1f2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, minWidth: 300 } }}
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>¿Eliminar regla personalizada?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se eliminará la regla de <strong>{item.nombre}</strong>. Volverá a usarse la regla predeterminada.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : null}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
const AdminComisiones = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [comisiones, setComisiones]   = useState([]);
  const [alertas, setAlertas]         = useState([]);
  const [resumen, setResumen]         = useState({});
  const [loading, setLoading]         = useState(true);
  const [loadingAlertas, setLoadingAlertas] = useState(true);
  const [error, setError]             = useState("");
  const [search, setSearch]           = useState("");

  // Filtros
  const [filtroEstatus, setFiltroEstatus]   = useState("");
  const [filtroTipo, setFiltroTipo]         = useState("");
  const [filtroAnticipo, setFiltroAnticipo] = useState("");
  const [filtroYear, setFiltroYear]         = useState(new Date().getFullYear());
  const [filtroMonth, setFiltroMonth]       = useState(0);

  // Dialogs
  const [detalle, setDetalle]               = useState(null);
  const [detalleNotas, setDetalleNotas]     = useState("");
  const [confirmDialog, setConfirmDialog]   = useState({ open: false, accion: "", comision: null });
  const [processing, setProcessing]         = useState(false);
  const [snack, setSnack]                   = useState({ show: false, msg: "", sev: "success" });

  // ── Estado del tab ────────────────────────────────────────────────────────────
  const [tabActivo, setTabActivo]           = useState(0);

  // ── Estado tab Configuración de Tarifas ──────────────────────────────────────
  const [reglas, setReglas]                 = useState([]);
  const [serviciosList, setServiciosList]   = useState([]);
  const [paquetesList, setPaquetesList]     = useState([]);
  const [loadingReglas, setLoadingReglas]   = useState(false);
  const [errorReglas, setErrorReglas]       = useState("");
  const [calculatorBase, setCalculatorBase] = useState(5000);

  // ── Cargar datos ─────────────────────────────────────────────────────────────
  const loadComisiones = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (filtroEstatus)  params.set("estatus", filtroEstatus);
      if (filtroTipo)     params.set("tipo", filtroTipo);
      if (filtroAnticipo) params.set("estado_anticipo", filtroAnticipo);
      if (filtroYear)     params.set("year", filtroYear);
      if (filtroMonth)    params.set("month", filtroMonth);

      const res = await api.get(`/comisiones?${params.toString()}`);
      setComisiones(res.data?.comisiones ?? []);
      setResumen(res.data?.resumen ?? {});
    } catch {
      setError("Error al cargar comisiones");
    } finally {
      setLoading(false);
    }
  }, [filtroEstatus, filtroTipo, filtroAnticipo, filtroYear, filtroMonth]);

  const loadAlertas = useCallback(async () => {
    setLoadingAlertas(true);
    try {
      const res = await api.get("/comisiones/alertas");
      setAlertas(res.data?.alertas ?? []);
    } catch {
      setAlertas([]);
    } finally {
      setLoadingAlertas(false);
    }
  }, []);

  const loadReglas = useCallback(async () => {
    setLoadingReglas(true);
    setErrorReglas("");
    try {
      const res = await api.get("/comisiones/reglas");
      setReglas(res.data?.reglas ?? []);
      setServiciosList(res.data?.servicios ?? []);
      setPaquetesList(res.data?.paquetes ?? []);
    } catch (e) {
      setErrorReglas(e?.response?.data?.message ?? "Error al cargar configuración de tarifas");
    } finally {
      setLoadingReglas(false);
    }
  }, []);

  useEffect(() => {
    loadComisiones();
    loadAlertas();
  }, [loadComisiones, loadAlertas]);

  useEffect(() => {
    if (tabActivo === 1) loadReglas();
  }, [tabActivo, loadReglas]);

  // Sincronizar notas editables cuando se abre el detalle
  useEffect(() => {
    if (detalle) setDetalleNotas(detalle.notas ?? "");
  }, [detalle]);

  // ── Acciones ──────────────────────────────────────────────────────────────────
  const ejecutarAccion = async () => {
    const { accion, comision } = confirmDialog;
    setProcessing(true);
    try {
      await api.put(`/comisiones?id=${comision.id}`, { accion });
      setSnack({
        show: true,
        msg: accion === "pagar_anticipo"
          ? "Anticipo registrado como pagado"
          : accion === "rechazar_anticipo"
          ? "Solicitud rechazada"
          : "Comisión marcada como pagada",
        sev: "success",
      });
      setConfirmDialog({ open: false, accion: "", comision: null });
      // Refrescar lista y actualizar el objeto detalle si sigue abierto
      const [res] = await Promise.all([api.get(`/comisiones?${new URLSearchParams({ year: filtroYear }).toString()}`), loadAlertas()]);
      const actualizadas = res.data?.comisiones ?? [];
      setComisiones(actualizadas);
      setResumen(res.data?.resumen ?? {});
      // Si había un detalle abierto para esta comisión, actualizarlo
      if (detalle && detalle.id === comision.id) {
        const fresca = actualizadas.find((c) => c.id === comision.id);
        if (fresca) setDetalle(fresca);
      }
    } catch {
      setSnack({ show: true, msg: "Error al procesar la acción", sev: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const guardarNotasDetalle = async () => {
    if (!detalle) return;
    setProcessing(true);
    try {
      await api.put(`/comisiones?id=${detalle.id}`, {
        accion: "actualizar_notas",
        notas: detalleNotas,
      });
      setSnack({ show: true, msg: "Notas guardadas", sev: "success" });
      setDetalle((prev) => prev ? { ...prev, notas: detalleNotas } : null);
      loadComisiones();
    } catch {
      setSnack({ show: true, msg: "Error al guardar notas", sev: "error" });
    } finally {
      setProcessing(false);
    }
  };

  // ── Filtrado local ───────────────────────────────────────────────────────────
  const filtradas = comisiones.filter((c) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      c.colaborador_nombre?.toLowerCase().includes(term) ||
      c.evento_titulo?.toLowerCase().includes(term) ||
      c.tipo?.toLowerCase().includes(term)
    );
  });

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const meses = [
    { v: 0, l: "Todos los meses" },
    { v: 1,  l: "Enero" }, { v: 2,  l: "Febrero" }, { v: 3,  l: "Marzo" },
    { v: 4,  l: "Abril" }, { v: 5,  l: "Mayo" },    { v: 6,  l: "Junio" },
    { v: 7,  l: "Julio" }, { v: 8,  l: "Agosto" },  { v: 9,  l: "Septiembre" },
    { v: 10, l: "Octubre" }, { v: 11, l: "Noviembre" }, { v: 12, l: "Diciembre" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            💰 Gestión de Comisiones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra anticipos y pagos de comisiones de colaboradores
          </Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={() => { loadComisiones(); loadAlertas(); if (tabActivo === 1) loadReglas(); }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <Tabs
          value={tabActivo}
          onChange={(_, v) => setTabActivo(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": { fontSize: "0.82rem", fontWeight: 600, textTransform: "none", minHeight: 48 },
            "& .Mui-selected": { color: "#64B5F6" },
            "& .MuiTabs-indicator": { bgcolor: "#64B5F6", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          <Tab label="Gestión" icon={<AttachMoney sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Configuración de Tarifas" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* TAB 0: GESTIÓN (contenido existente)                               */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {tabActivo === 0 && (
        <>
          {/* ── Banner de alertas ───────────────────────────────────────────────── */}
      {!loadingAlertas && alertas.length > 0 && (
        <Alert
          severity="warning"
          icon={<NotificationsActive />}
          sx={{
            mb: 3, borderRadius: 2,
            bgcolor: "rgba(255,152,0,0.1)",
            border: "1px solid rgba(255,152,0,0.4)",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            ⚠️ {alertas.length} solicitud{alertas.length > 1 ? "es" : ""} de anticipo pendiente{alertas.length > 1 ? "s" : ""} de aprobación
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {alertas.map((a) => (
              <Chip
                key={a.id}
                avatar={<Avatar sx={{ bgcolor: "#FF9800", fontSize: 12 }}>{a.colaborador_nombre?.[0]}</Avatar>}
                label={`${a.colaborador_nombre} — ${fmt(a.anticipo_permitido)} (${a.evento_titulo})`}
                onClick={() => {
                  setConfirmDialog({ open: true, accion: "pagar_anticipo", comision: a });
                }}
                sx={{ bgcolor: "rgba(255,152,0,0.15)", color: "#FFB74D", cursor: "pointer",
                  "&:hover": { bgcolor: "rgba(255,152,0,0.25)" } }}
              />
            ))}
          </Box>
        </Alert>
      )}

      {/* ── KPIs ────────────────────────────────────────────────────────────── */}
      <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ mb: 3 }}>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard icon={<AttachMoney />} label="Total generado"
            value={fmt(resumen.monto_total)} color="#64B5F6" />
        </Grid>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard icon={<HourglassEmpty />} label="Pendientes"
            value={resumen.comisiones_pendientes ?? 0} color="#FFA726"
            sub={`${resumen.anticipos_solicitados ?? 0} anticipo(s)`} />
        </Grid>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard icon={<CheckCircle />} label="Pagadas"
            value={resumen.comisiones_pagadas ?? 0} color="#66BB6A" />
        </Grid>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <Badge badgeContent={alertas.length} color="error">
            <KpiCard icon={<NotificationsActive />} label="Anticipos solicitados"
              value={resumen.anticipos_solicitados ?? 0} color="#EF5350"
              sub="Requieren aprobación" />
          </Badge>
        </Grid>
      </Grid>

      {/* ── Filtros ─────────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <FilterList fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Filtros</Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" placeholder="Buscar colaborador o evento..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estatus</InputLabel>
              <Select value={filtroEstatus} label="Estatus" onChange={(e) => setFiltroEstatus(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
                <MenuItem value="Pagada">Pagada</MenuItem>
                <MenuItem value="Cancelado">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={filtroTipo} label="Tipo" onChange={(e) => setFiltroTipo(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="comision">Comisión</MenuItem>
                <MenuItem value="dia_laborado">Día Laborado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Anticipo</InputLabel>
              <Select value={filtroAnticipo} label="Anticipo" onChange={(e) => setFiltroAnticipo(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="solicitado">Solicitado</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="pagado">Pagado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select value={filtroYear} label="Año" onChange={(e) => setFiltroYear(e.target.value)}>
                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select value={filtroMonth} label="Mes" onChange={(e) => setFiltroMonth(e.target.value)}>
                {meses.map((m) => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Tabla / Tarjetas ────────────────────────────────────────────── */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading && <LinearProgress />}

        {/* ── Vista Móvil: Tarjetas ── */}
        {isMobile ? (
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {!loading && filtradas.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                Sin comisiones para los filtros seleccionados
              </Typography>
            )}
            {filtradas.map((c) => (
              <Card
                key={c.id}
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: "12px !important" }}>
                  {/* Colaborador + tipo */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: "#1565C0", flexShrink: 0 }}>
                        {c.colaborador_nombre?.[0] ?? "?"}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.colaborador_nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                          {c.evento_titulo}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={c.tipo === "dia_laborado" ? "Día Lab." : "Comisión"}
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        ml: 1,
                        flexShrink: 0,
                        bgcolor: c.tipo === "dia_laborado" ? "rgba(103,58,183,0.15)" : "rgba(30,136,229,0.15)",
                        color: c.tipo === "dia_laborado" ? "#CE93D8" : "#90CAF9",
                      }}
                    />
                  </Box>

                  {/* Montos + estado */}
                  <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary">Monto total</Typography>
                      <Typography sx={{ fontWeight: 700, color: "#E0E0E0", fontSize: "0.9rem" }}>{fmt(c.monto)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary">Anticipo 25%</Typography>
                      <Typography sx={{ fontWeight: 700, color: "#FFC107", fontSize: "0.9rem" }}>{fmt(c.anticipo_permitido)}</Typography>
                    </Box>
                  </Box>

                  {/* Chips de estado */}
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 0.5 }}>
                    <StatusChip value={c.estatus} map={ESTATUS_CONFIG} />
                    <StatusChip value={c.estado_anticipo} map={ANTICIPO_CONFIG} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">{fmtDate(c.fecha_evento)}</Typography>
                </CardContent>
                <CardActions sx={{ px: 1.5, pt: 0, pb: 1, gap: 0.5, flexWrap: "wrap" }}>
                  <Button size="small" startIcon={<Visibility fontSize="small" />}
                    onClick={() => setDetalle(c)} sx={{ fontSize: "0.72rem" }}>
                    Detalle
                  </Button>
                  {c.estado_anticipo !== "pagado" && c.estatus !== "Cancelado" && c.estatus !== "Pagada" && (
                    <Button size="small" startIcon={<ThumbUp fontSize="small" />}
                      sx={{ fontSize: "0.72rem", color: c.estado_anticipo === "solicitado" ? "#66BB6A" : "#FFC107" }}
                      onClick={() => setConfirmDialog({ open: true, accion: "pagar_anticipo", comision: c })}>
                      {c.estado_anticipo === "solicitado" ? "Aprobar" : "Anticipo"}
                    </Button>
                  )}
                  {c.estado_anticipo === "solicitado" && (
                    <Button size="small" startIcon={<ThumbDown fontSize="small" />}
                      sx={{ fontSize: "0.72rem", color: "#EF5350" }}
                      onClick={() => setConfirmDialog({ open: true, accion: "rechazar_anticipo", comision: c })}>
                      Rechazar
                    </Button>
                  )}
                  {c.estatus !== "Pagada" && c.estatus !== "Cancelado" && (
                    <Button size="small" startIcon={<Payment fontSize="small" />}
                      sx={{ fontSize: "0.72rem", color: "#FFC107" }}
                      onClick={() => setConfirmDialog({ open: true, accion: "marcar_pagada", comision: c })}>
                      Marcar pagada
                    </Button>
                  )}
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          /* ── Vista Desktop: Tabla ── */
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(255,255,255,0.04)" }}>
                  {["Colaborador", "Evento", "Tipo", "Monto Total", "Anticipo 25%", "Estado Anticipo", "Estatus", "Fecha", "Acciones"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", py: 1.5, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && filtradas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">Sin comisiones para los filtros seleccionados</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filtradas.map((c) => (
                  <TableRow key={c.id} hover sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.03)" } }}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#1565C0" }}>
                          {c.colaborador_nombre?.[0] ?? "?"}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.82rem" }}>
                          {c.colaborador_nombre}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.82rem", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.evento_titulo}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{fmtDate(c.fecha_evento)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.tipo === "dia_laborado" ? "Día Laborado" : "Comisión"}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          bgcolor: c.tipo === "dia_laborado" ? "rgba(103,58,183,0.15)" : "rgba(30,136,229,0.15)",
                          color: c.tipo === "dia_laborado" ? "#CE93D8" : "#90CAF9",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: "#E0E0E0", fontSize: "0.88rem" }}>
                        {fmt(c.monto)}
                      </Typography>
                      {c.porcentaje && (
                        <Typography variant="caption" color="text.secondary">{c.porcentaje}%</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: "#FFC107", fontSize: "0.85rem" }}>
                        {fmt(c.anticipo_permitido)}
                      </Typography>
                      {c.anticipo_pagado > 0 && (
                        <Typography variant="caption" color="#66BB6A">Pagado: {fmt(c.anticipo_pagado)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip value={c.estado_anticipo} map={ANTICIPO_CONFIG} />
                      {c.estado_anticipo === "solicitado" && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3 }}>
                          {fmtDate(c.fecha_anticipo)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip value={c.estatus} map={ESTATUS_CONFIG} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{fmtDate(c.fecha_generacion)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => setDetalle(c)}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        {c.estado_anticipo !== "pagado" && c.estatus !== "Cancelado" && c.estatus !== "Pagada" && (
                          <Tooltip title={c.estado_anticipo === "solicitado" ? "Aprobar anticipo solicitado" : "Pagar anticipo (25%)"}>
                            <IconButton size="small" sx={{ color: c.estado_anticipo === "solicitado" ? "#66BB6A" : "#FFC107" }}
                              onClick={() => setConfirmDialog({ open: true, accion: "pagar_anticipo", comision: c })}>
                              <ThumbUp fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {c.estado_anticipo === "pagado" && c.estatus !== "Cancelado" && c.estatus !== "Pagada" && (
                          <Tooltip title="Anticipo ya pagado">
                            <span>
                              <IconButton size="small" disabled sx={{ color: "#4CAF50", opacity: 0.5 }}>
                                <ThumbUp fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {c.estado_anticipo === "solicitado" && (
                          <Tooltip title="Rechazar solicitud">
                            <IconButton size="small" sx={{ color: "#EF5350" }}
                              onClick={() => setConfirmDialog({ open: true, accion: "rechazar_anticipo", comision: c })}>
                              <ThumbDown fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {c.estatus !== "Pagada" && (
                          <Tooltip title="Marcar como pagada">
                            <IconButton size="small" sx={{ color: "#FFC107" }}
                              onClick={() => setConfirmDialog({ open: true, accion: "marcar_pagada", comision: c })}>
                              <Payment fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && (
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <Typography variant="caption" color="text.secondary">
              {filtradas.length} comisión{filtradas.length !== 1 ? "es" : ""} mostrada{filtradas.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
      </Paper>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* TAB 1: CONFIGURACIÓN DE TARIFAS                                    */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {tabActivo === 1 && (
        <>
          {loadingReglas ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : errorReglas ? (
            <Alert severity="error" sx={{ borderRadius: 2 }} action={
              <Button color="inherit" size="small" onClick={loadReglas}>Reintentar</Button>
            }>
              {errorReglas}
            </Alert>
          ) : (
            <>
              {/* ── Header del tab ─────────────────────────────────────── */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 4, flexWrap: "wrap", gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    ⚙️ Configuración de Tarifas
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                    Define el porcentaje o monto fijo de comisión para cada paquete o servicio.
                    Los elementos sin regla específica usan la regla predeterminada.
                  </Typography>
                </Box>

                {/* Calculadora de vista previa */}
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    bgcolor: "rgba(100,181,246,0.05)",
                    border: "1px solid rgba(100,181,246,0.2)",
                    borderRadius: 2, p: 1.5, flexShrink: 0,
                  }}
                >
                  <Calculate sx={{ color: "#64B5F6", fontSize: 22 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
                      Valor de ejemplo para preview
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={calculatorBase}
                      onChange={(e) => setCalculatorBase(Math.max(0, parseFloat(e.target.value) || 0))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        sx: { fontSize: "0.85rem" },
                      }}
                      sx={{ width: 140 }}
                    />
                  </Box>
                </Paper>
              </Box>

              {/* ── Regla Predeterminada (Global) ─────────────────────── */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TrendingUp sx={{ color: "#64B5F6", fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Regla Predeterminada</Typography>
                  <Chip
                    label="Aplica a todo lo que no tenga regla específica"
                    size="small"
                    sx={{ bgcolor: "rgba(100,181,246,0.1)", color: "#64B5F6", fontSize: "0.65rem", height: 20 }}
                  />
                </Box>
                <Grid container>
                  <Grid item xs={12} sm={8} md={5}>
                    <ReglaCard
                      item={{ id: null, nombre: "Regla Global", precio: null }}
                      tipoRef="global"
                      regla={reglas.find((r) => r.tipo_referencia === "global") ?? null}
                      globalRegla={null}
                      calculatorBase={calculatorBase}
                      onSaved={loadReglas}
                      onSnack={setSnack}
                      isGlobal
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(255,255,255,0.08)" }} />

              {/* ── Paquetes ──────────────────────────────────────────── */}
              {paquetesList.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                    <Inventory sx={{ color: "#81C784", fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Paquetes</Typography>
                    <Chip
                      label={`${paquetesList.filter((p) => reglas.some((r) => r.tipo_referencia === "paquete" && String(r.referencia_id) === String(p.id))).length} / ${paquetesList.length} con regla propia`}
                      size="small"
                      sx={{ bgcolor: "rgba(129,199,132,0.1)", color: "#81C784", fontSize: "0.65rem", height: 20 }}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    {paquetesList.map((p) => (
                      <Grid item xs={12} sm={6} md={4} key={p.id}>
                        <ReglaCard
                          item={p}
                          tipoRef="paquete"
                          regla={reglas.find((r) => r.tipo_referencia === "paquete" && String(r.referencia_id) === String(p.id)) ?? null}
                          globalRegla={reglas.find((r) => r.tipo_referencia === "global") ?? null}
                          calculatorBase={calculatorBase}
                          onSaved={loadReglas}
                          onSnack={setSnack}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* ── Servicios ─────────────────────────────────────────── */}
              {serviciosList.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                    <Build sx={{ color: "#CE93D8", fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Servicios</Typography>
                    <Chip
                      label={`${serviciosList.filter((s) => reglas.some((r) => r.tipo_referencia === "servicio" && String(r.referencia_id) === String(s.id))).length} / ${serviciosList.length} con regla propia`}
                      size="small"
                      sx={{ bgcolor: "rgba(206,147,216,0.1)", color: "#CE93D8", fontSize: "0.65rem", height: 20 }}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    {serviciosList.map((s) => (
                      <Grid item xs={12} sm={6} md={4} key={s.id}>
                        <ReglaCard
                          item={s}
                          tipoRef="servicio"
                          regla={reglas.find((r) => r.tipo_referencia === "servicio" && String(r.referencia_id) === String(s.id)) ?? null}
                          globalRegla={reglas.find((r) => r.tipo_referencia === "global") ?? null}
                          calculatorBase={calculatorBase}
                          onSaved={loadReglas}
                          onSnack={setSnack}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Estado vacío */}
              {paquetesList.length === 0 && serviciosList.length === 0 && (
                <Paper sx={{ p: 5, textAlign: "center", borderRadius: 2, bgcolor: "rgba(255,255,255,0.02)" }}>
                  <Typography color="text.secondary">
                    No hay paquetes ni servicios registrados. Ve a la sección <strong>Servicios</strong> para crearlos.
                  </Typography>
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {/* ── Dialog Detalle ──────────────────────────────────────────────────── */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: "#1a1f2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: { xs: 0, sm: 3 } } }}>
        {detalle && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AttachMoney sx={{ color: "#FFC107" }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalle de Comisión #{detalle.id}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setDetalle(null)}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <StatusChip value={detalle.estatus} map={ESTATUS_CONFIG} />
                    <StatusChip value={detalle.estado_anticipo} map={ANTICIPO_CONFIG} />
                    <Chip label={detalle.tipo === "dia_laborado" ? "Día Laborado" : "Comisión"} size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.07)", fontSize: "0.72rem" }} />
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Person fontSize="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">Colaborador</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{detalle.colaborador_nombre}</Typography>
                  <Typography variant="caption" color="text.secondary">{detalle.colaborador_email}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <EventNote fontSize="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">Evento</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{detalle.evento_titulo}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(detalle.fecha_evento)}</Typography>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                {[
                  { l: "Monto Total",        v: fmt(detalle.monto),               color: "#E0E0E0" },
                  { l: "Porcentaje",          v: detalle.porcentaje ? `${detalle.porcentaje}%` : "—", color: "#90CAF9" },
                  { l: "Anticipo (25%)",      v: fmt(detalle.anticipo_permitido),  color: "#FFC107" },
                  { l: "Anticipo Pagado",     v: fmt(detalle.anticipo_pagado),     color: "#66BB6A" },
                  { l: "Fecha Generación",    v: fmtDate(detalle.fecha_generacion), color: "#9E9E9E" },
                  { l: "Fecha Anticipo",      v: fmtDate(detalle.fecha_anticipo),   color: "#9E9E9E" },
                  { l: "Fecha Pago Final",    v: fmtDate(detalle.fecha_pago),       color: "#9E9E9E" },
                ].map(({ l, v, color }) => (
                  <Grid item xs={6} key={l}>
                    <Typography variant="caption" color="text.secondary">{l}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color }}>{v}</Typography>
                  </Grid>
                ))}

                {detalle.descripcion && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Descripción</Typography>
                    <Typography variant="body2">{detalle.descripcion}</Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline rows={3}
                    label="Notas (solo administrador)"
                    value={detalleNotas}
                    onChange={(e) => setDetalleNotas(e.target.value)}
                    size="small"
                    placeholder="Agrega información relevante sobre esta comisión..."
                  />
                </Grid>

                {/* Barra de progreso pago */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">Progreso de pago</Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "#FFC107" }}>Anticipo (25%)</Typography>
                    <Typography variant="caption" sx={{ color: "#9E9E9E" }}>Restante (75%)</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={detalle.estatus === "Pagada" ? 100 : detalle.estado_anticipo === "pagado" ? 25 : 0}
                    sx={{ height: 8, borderRadius: 4,
                      bgcolor: "rgba(255,255,255,0.1)",
                      "& .MuiLinearProgress-bar": { bgcolor: detalle.estatus === "Pagada" ? "#66BB6A" : "#FFC107" }
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" disabled={processing}
                onClick={guardarNotasDetalle}>
                {processing ? <CircularProgress size={14} /> : "Guardar Notas"}
              </Button>
              {detalle.estado_anticipo !== "pagado" && detalle.estatus !== "Cancelado" && detalle.estatus !== "Pagada" && (
                <Button variant="contained" size="small"
                  sx={{ bgcolor: "#FFC107", color: "#000", "&:hover": { bgcolor: "#FFB300" } }}
                  onClick={() => setConfirmDialog({ open: true, accion: "pagar_anticipo", comision: detalle })}>
                  Pagar Anticipo (25%)
                </Button>
              )}
              {detalle.estado_anticipo === "pagado" && detalle.estatus !== "Pagada" && detalle.estatus !== "Cancelado" && (
                <Button variant="outlined" size="small" disabled
                  sx={{ borderColor: "#4CAF50", color: "#4CAF50", opacity: 0.6 }}>
                  ✓ Anticipo Pagado
                </Button>
              )}
              {detalle.estado_anticipo === "solicitado" && (
                <Button variant="outlined" size="small" color="error"
                  onClick={() => setConfirmDialog({ open: true, accion: "rechazar_anticipo", comision: detalle })}>
                  Rechazar Solicitud
                </Button>
              )}
              {detalle.estatus !== "Pagada" && detalle.estatus !== "Cancelado" && (
                <Button variant="contained" size="small" color="success"
                  onClick={() => setConfirmDialog({ open: true, accion: "marcar_pagada", comision: detalle })}>
                  Marcar Pagada
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Dialog Confirmación ─────────────────────────────────────────────── */}
      <Dialog open={confirmDialog.open} onClose={() => !processing && setConfirmDialog({ open: false, accion: "", comision: null })}
        PaperProps={{ sx: { bgcolor: "#1a1f2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, minWidth: 340 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning sx={{ color: "#FFC107" }} />
          {confirmDialog.accion === "pagar_anticipo"  && "Confirmar pago de anticipo"}
          {confirmDialog.accion === "rechazar_anticipo" && "Rechazar solicitud de anticipo"}
          {confirmDialog.accion === "marcar_pagada"   && "Marcar comisión como pagada"}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.comision && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Colaborador:</strong> {confirmDialog.comision.colaborador_nombre}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Evento:</strong> {confirmDialog.comision.evento_titulo}
              </Typography>
              {confirmDialog.accion === "pagar_anticipo" && (
                <Typography variant="body2">
                  <strong>Monto anticipo:</strong>{" "}
                  <span style={{ color: "#FFC107", fontWeight: 700 }}>
                    {fmt(confirmDialog.comision.anticipo_permitido)}
                  </span>
                </Typography>
              )}
              {confirmDialog.accion === "marcar_pagada" && (
                <Typography variant="body2">
                  <strong>Monto total:</strong>{" "}
                  <span style={{ color: "#66BB6A", fontWeight: 700 }}>
                    {fmt(confirmDialog.comision.monto)}
                  </span>
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmDialog({ open: false, accion: "", comision: null })} disabled={processing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={ejecutarAccion}
            disabled={processing}
            color={confirmDialog.accion === "rechazar_anticipo" ? "error" : "success"}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            {processing ? "Procesando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>



      {/* ── Snackbar ────────────────────────────────────────────────────────── */}
      {snack.show && (
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ ...snack, show: false })}
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, minWidth: 280, borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      )}
    </Box>
  );
};

export default AdminComisiones;
